// Local encrypted-at-rest store. Conversation metadata, decrypted message bodies,
// ratchet sessions, and group keys are sealed under `storeKey` (derived from the
// password) before they touch IndexedDB, so disk access alone reveals nothing. The
// small `account` record (already-encrypted key bundle, salt, bearer token) is the
// only thing readable before unlock - it's what we need to unlock.
import { openDB, type IDBPDatabase } from "idb";
import { toB64, fromB64, utf8, fromUtf8 } from "@shared/encoding.js";
import { aeadSeal, aeadOpen } from "../crypto/index.js";
import type { SerializedRatchet, SerializedSender, SerializedReceiver } from "../crypto/index.js";

const DB_NAME = "hush";
const DB_VERSION = 1;

interface Envelope {
  n: string;
  c: string;
}

export interface AccountRecord {
  user_id: string;
  handle: string;
  token: string;
  salt: string;
  key_bundle: string; // already encrypted under wrapKey
}

export interface StoredConversation {
  conv_id: string;
  type: "dm" | "group";
  title: string | null; // group name; DMs derive title from the peer handle
  members: { user_id: string; handle: string; ik_dh_pub: string; ik_sig_pub: string; role: string }[];
  disappearing_ttl: number;
  last_seq: number;
  last_keydist_id: number;
  last_tomb: number;
}

export interface StoredFile {
  name: string;
  mime: string;
  size: number;
  blob_key: string;
  key: string; // base64 per-attachment symmetric key
}

export interface StoredMessage {
  msg_id: string;
  conv_id: string;
  seq: number;
  sender_id: string;
  outgoing: boolean;
  kind: "text" | "file" | "system";
  text?: string;
  file?: StoredFile;
  ts: number;
  expires_at: number | null;
}

export interface StoredSession {
  peer_id: string;
  state: SerializedRatchet;
  established: boolean;
  // X3DH init to re-attach to outgoing headers until the peer replies (b64 fields).
  pendingInit?: { ik_dh: string; ik_sig: string; ek: string; spk_id: number; opk_id: number | null };
}

export interface StoredGroup {
  conv_id: string;
  epoch: number;
  mySender: SerializedSender | null;
  receivers: Record<string, SerializedReceiver>;
}

export interface Settings {
  // Each of these makes you broadcast a little metadata. Off by default; you only ever
  // share what you opt into, and you always see what others choose to share.
  readReceipts: boolean;
  presence: boolean;
  typing: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  readReceipts: false,
  presence: false,
  typing: false,
};

function dbPromise(): Promise<IDBPDatabase> {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      db.createObjectStore("account");
      db.createObjectStore("conversations", { keyPath: "conv_id" });
      const msgs = db.createObjectStore("messages", { keyPath: "msg_id" });
      msgs.createIndex("by_conv", "conv_id");
      db.createObjectStore("sessions", { keyPath: "peer_id" });
      db.createObjectStore("groups", { keyPath: "conv_id" });
      db.createObjectStore("kv");
    },
  });
}

// ---- Account (unencrypted, pre-unlock) ----

export async function readAccount(): Promise<AccountRecord | null> {
  const db = await dbPromise();
  return (await db.get("account", "self")) ?? null;
}
export async function writeAccount(rec: AccountRecord): Promise<void> {
  const db = await dbPromise();
  await db.put("account", rec, "self");
}

/** Full wipe - used on logout and on "purge all / this device". */
export async function wipeStore(): Promise<void> {
  const db = await dbPromise();
  await Promise.all(["account", "conversations", "messages", "sessions", "groups", "kv"].map((s) => db.clear(s)));
}

// ---- Encrypted store, available after unlock ----

export class LocalStore {
  private constructor(
    private db: IDBPDatabase,
    private storeKey: Uint8Array,
  ) {}

  static async open(storeKey: Uint8Array): Promise<LocalStore> {
    return new LocalStore(await dbPromise(), storeKey);
  }

  private seal(value: unknown): Envelope {
    const { nonce, ct } = aeadSeal(this.storeKey, utf8(JSON.stringify(value)));
    return { n: toB64(nonce), c: toB64(ct) };
  }
  private unseal<T>(env: Envelope): T {
    return JSON.parse(fromUtf8(aeadOpen(this.storeKey, fromB64(env.n), fromB64(env.c)))) as T;
  }

  // Conversations
  async putConversation(c: StoredConversation): Promise<void> {
    await this.db.put("conversations", { conv_id: c.conv_id, env: this.seal(c) });
  }
  async getConversation(id: string): Promise<StoredConversation | null> {
    const row = await this.db.get("conversations", id);
    return row ? this.unseal<StoredConversation>(row.env) : null;
  }
  async allConversations(): Promise<StoredConversation[]> {
    const rows = await this.db.getAll("conversations");
    return rows.map((r) => this.unseal<StoredConversation>(r.env));
  }

  // Messages
  async putMessage(m: StoredMessage): Promise<void> {
    await this.db.put("messages", { msg_id: m.msg_id, conv_id: m.conv_id, seq: m.seq, env: this.seal(m) });
  }
  async getMessage(msgId: string): Promise<StoredMessage | null> {
    const row = await this.db.get("messages", msgId);
    return row ? this.unseal<StoredMessage>(row.env) : null;
  }
  async messagesFor(convId: string): Promise<StoredMessage[]> {
    const rows = await this.db.getAllFromIndex("messages", "by_conv", convId);
    return rows.map((r) => this.unseal<StoredMessage>(r.env)).sort((a, b) => a.seq - b.seq || a.ts - b.ts);
  }
  async deleteMessage(msgId: string): Promise<void> {
    await this.db.delete("messages", msgId);
  }
  /** Local-only purge by predicate over the decrypted message. Returns removed ids. */
  async purgeMessages(convId: string, keepIf: (m: StoredMessage) => boolean): Promise<string[]> {
    const rows = await this.db.getAllFromIndex("messages", "by_conv", convId);
    const removed: string[] = [];
    const tx = this.db.transaction("messages", "readwrite");
    for (const r of rows) {
      const m = this.unseal<StoredMessage>(r.env);
      if (!keepIf(m)) {
        removed.push(m.msg_id);
        await tx.store.delete(m.msg_id);
      }
    }
    await tx.done;
    return removed;
  }

  // Pairwise ratchet sessions (keyed by peer user id)
  async getSession(peerId: string): Promise<StoredSession | null> {
    const row = await this.db.get("sessions", peerId);
    return row ? this.unseal<StoredSession>(row.env) : null;
  }
  async putSession(s: StoredSession): Promise<void> {
    await this.db.put("sessions", { peer_id: s.peer_id, env: this.seal(s) });
  }

  // Group sender-key state
  async getGroup(convId: string): Promise<StoredGroup | null> {
    const row = await this.db.get("groups", convId);
    return row ? this.unseal<StoredGroup>(row.env) : null;
  }
  async putGroup(g: StoredGroup): Promise<void> {
    await this.db.put("groups", { conv_id: g.conv_id, env: this.seal(g) });
  }

  // Settings
  async getSettings(): Promise<Settings> {
    const row = await this.db.get("kv", "settings");
    return row ? { ...DEFAULT_SETTINGS, ...this.unseal<Settings>(row) } : { ...DEFAULT_SETTINGS };
  }
  async putSettings(s: Settings): Promise<void> {
    await this.db.put("kv", this.seal(s), "settings");
  }
}
